import React, { useState, useEffect } from 'react';
import { api } from '../api';

/**
 * One-click "create invoice in Zoho Books" button for the Reports page.
 *
 * Props:
 *   type:        'daily_used_cars' | 'weekly_department'
 *   department:  Zoho department code — 'OFFICE' | 'SERVICE' | 'KIA' |
 *                'SUZUKI' | 'GWM' | 'GAC' | 'USED_CARS'
 *   workDate:    'YYYY-MM-DD' string (for daily: the day; for weekly: any
 *                date inside the week — backend derives the week #)
 *   items:       Array of { service, quantity, rate } — taken straight from
 *                the on-screen paperwork. Backend normalises aliases.
 *   expectedTotalExGst: number — sum of (qty × rate) on screen. Backend
 *                aborts if its own sum disagrees by >$0.01 (catches the
 *                "screen state drift" footgun).
 *   label:       Optional override for the button text. Default "📤 Send to Zoho".
 *   onCreated:   Optional callback(result) after a successful create.
 *
 * UX:
 *   Click → confirm modal (shows invoice number, subject, OP, totals).
 *   Confirm → POST /api/zoho/create-invoice → result modal with link to Zoho.
 *   Refuses if Zoho isn't configured on the server (503 from backend).
 *   Surfaces the "already exists" path so the boss can open the existing
 *   invoice instead of being told it failed.
 */
// The invoice number and subject come from the server (GET
// /api/zoho/invoice-meta). They used to be worked out here as well, which
// meant the numbering rule lived in two files — and when Melville asked for
// shorter numbers, one of them would have been missed.

export default function SendToZohoButton({
  type, department, workDate, items, expectedTotalExGst,
  label = '📤 Send to Zoho', onCreated,
}) {
  const [showConfirm, setShowConfirm] = useState(false);
  const [showResult,  setShowResult]  = useState(null);   // success/error data
  const [busy,        setBusy]        = useState(false);
  const [meta,        setMeta]        = useState(null);

  const ready = !!(items && items.length && workDate);

  // Declared before the early return below — hooks can't sit behind a branch.
  useEffect(() => {
    if (!ready) { setMeta(null); return undefined; }
    let live = true;
    api.zohoInvoiceMeta({ type, department: department || '', workDate })
      .then(m => { if (live) setMeta(m); })
      .catch(() => { if (live) setMeta(null); });
    return () => { live = false; };
  }, [ready, type, department, workDate]);

  if (!ready) {
    return (
      <button disabled title="No items to send"
        style={btn({ opacity: 0.4, cursor: 'not-allowed' })}>
        {label}
      </button>
    );
  }

  const totalEx  = expectedTotalExGst || items.reduce((s, i) => s + (parseFloat(i.quantity) || 0) * (parseFloat(i.rate) || 0), 0);
  const totalGst = totalEx * 0.10;
  const totalInc = totalEx + totalGst;

  async function handleConfirm() {
    setBusy(true);
    try {
      const res = await api.createZohoInvoice({
        type, department, workDate, items, expectedTotalExGst: totalEx,
      });
      setShowConfirm(false);
      if (res.success) {
        setShowResult({ ok: true, data: res });
        if (onCreated) onCreated(res);
      } else {
        setShowResult({ ok: false, error: res.error || 'Failed to create invoice', existing: res.existing });
      }
    } catch (err) {
      // request() throws on non-ok responses — message comes from backend
      let existing = null;
      try {
        // The thrown error in api.js currently strips structured fields, so
        // we try to detect "already exists" via the message.
        if (err.message && err.message.toLowerCase().includes('already exists')) {
          existing = { invoice_number: meta?.invoice_number };
        }
      } catch { /* ignore */ }
      setShowResult({ ok: false, error: err.message || 'Network error', existing });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button
        onClick={() => setShowConfirm(true)}
        disabled={busy}
        style={btn(busy ? { opacity: 0.6, cursor: 'wait' } : {})}
      >
        {busy ? '⏳ Sending…' : label}
      </button>

      {showConfirm && (
        <Modal onClose={() => !busy && setShowConfirm(false)}>
          <div style={modalHeader}>📤 Create invoice in Zoho?</div>
          <div style={modalBody}>
            <div style={metaRow}><span>Invoice #</span><strong>{meta?.invoice_number || '…'}</strong></div>
            <div style={metaRow}><span>Subject</span><strong>{meta?.subject_content || '…'}</strong></div>
            {meta?.reference_number && (
              <div style={metaRow}>
                <span>OP / Reference</span>
                <strong style={{ color: '#92400E' }}>{meta.reference_number}</strong>
              </div>
            )}
            <div style={metaRow}><span>Work date</span><strong>{workDate.split('-').reverse().join('/')}</strong></div>
            <div style={{ ...metaRow, marginTop: 8 }}><span>Line items</span><strong>{items.length}</strong></div>
            <div style={metaRow}><span>Sub-total (ex GST)</span><strong>{fmtMoney(totalEx)}</strong></div>
            <div style={metaRow}><span>+ GST 10%</span><strong>{fmtMoney(totalGst)}</strong></div>
            <div style={{ ...metaRow, fontSize: 15, marginTop: 6, paddingTop: 8, borderTop: '1px solid #E5E7EB' }}>
              <span>Total inc GST</span><strong style={{ color: '#16a34a' }}>{fmtMoney(totalInc)}</strong>
            </div>
            {meta?.reference_number && (
              <div style={{ background: '#FEF3C7', border: '1px solid #FCD34D', padding: '6px 10px', borderRadius: 6, fontSize: 11, color: '#92400E', marginTop: 10 }}>
                ⚠ OP <strong>{meta.reference_number}</strong> é placeholder. Atualize no Zoho antes de enviar pro Andrew.
              </div>
            )}
          </div>
          <div style={modalFooter}>
            <button onClick={() => setShowConfirm(false)} disabled={busy} style={btnGhost}>Cancel</button>
            <button onClick={handleConfirm} disabled={busy} style={btnPrimary}>
              {busy ? '⏳ Creating…' : '✓ Criar invoice'}
            </button>
          </div>
        </Modal>
      )}

      {showResult && (
        <Modal onClose={() => setShowResult(null)}>
          {showResult.ok ? (
            <>
              <div style={{ ...modalHeader, color: '#15803D' }}>
                ✅ Invoice criado!
              </div>
              <div style={modalBody}>
                <div style={metaRow}><span>Invoice #</span><strong>{showResult.data.invoice_number}</strong></div>
                <div style={metaRow}><span>Subject</span><strong>{showResult.data.subject}</strong></div>
                {showResult.data.reference_number && (
                  <div style={metaRow}>
                    <span>OP</span>
                    <strong style={{ color: '#92400E' }}>{showResult.data.reference_number}</strong>
                  </div>
                )}
                <div style={metaRow}><span>Sub-total</span><strong>{fmtMoney(showResult.data.sub_total)}</strong></div>
                <div style={metaRow}><span>GST</span><strong>{fmtMoney(showResult.data.tax_total)}</strong></div>
                <div style={{ ...metaRow, fontSize: 15, marginTop: 6, paddingTop: 8, borderTop: '1px solid #E5E7EB' }}>
                  <span>Total</span><strong style={{ color: '#16a34a' }}>{fmtMoney(showResult.data.total)}</strong>
                </div>
              </div>
              <div style={modalFooter}>
                <a href={showResult.data.zoho_url} target="_blank" rel="noopener noreferrer"
                  style={{ ...btnPrimary, textDecoration: 'none', display: 'inline-block' }}>
                  Abrir no Zoho ↗
                </a>
                <button onClick={() => setShowResult(null)} style={btnGhost}>OK</button>
              </div>
            </>
          ) : (
            <>
              <div style={{ ...modalHeader, color: '#991B1B' }}>⚠ Não foi possível criar</div>
              <div style={{ ...modalBody, color: '#991B1B' }}>
                {showResult.error}
                {showResult.existing && (
                  <div style={{ background: '#FEF3C7', border: '1px solid #FCD34D', padding: 10, borderRadius: 6, marginTop: 10, fontSize: 12, color: '#92400E' }}>
                    Já existe uma invoice com este número no Zoho.
                    {showResult.existing.zoho_url && (
                      <> <a href={showResult.existing.zoho_url} target="_blank" rel="noopener noreferrer" style={{ color: '#0F172A', fontWeight: 700 }}>Ver no Zoho ↗</a></>
                    )}
                  </div>
                )}
              </div>
              <div style={modalFooter}>
                <button onClick={() => setShowResult(null)} style={btnPrimary}>OK</button>
              </div>
            </>
          )}
        </Modal>
      )}
    </>
  );
}

// ── Tiny modal primitive ───────────────────────────────────────────────────
function Modal({ onClose, children }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 800,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 12, width: '100%', maxWidth: 480,
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)', display: 'flex', flexDirection: 'column',
        }}
      >
        {children}
      </div>
    </div>
  );
}

function fmtMoney(n) { return '$' + (Number(n) || 0).toFixed(2); }

const btn = (extra = {}) => ({
  padding: '4px 14px', borderRadius: 0, cursor: 'pointer',
  border: '1px solid #92400E', background: '#FEF3C7', color: '#92400E',
  fontWeight: 700, fontSize: 11, whiteSpace: 'nowrap', ...extra,
});

const modalHeader = { padding: '16px 20px', borderBottom: '1px solid #E5E7EB', fontSize: 16, fontWeight: 800, color: '#0F172A' };
const modalBody   = { padding: '16px 20px', fontSize: 13 };
const modalFooter = { padding: '12px 20px', borderTop: '1px solid #E5E7EB', display: 'flex', gap: 8, justifyContent: 'flex-end' };
const metaRow     = { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '4px 0', color: '#475569' };
const btnPrimary  = { padding: '8px 18px', borderRadius: 8, border: 'none', background: '#1E3A4A', color: '#fff', fontWeight: 800, fontSize: 13, cursor: 'pointer' };
const btnGhost    = { padding: '8px 16px', borderRadius: 8, border: '1px solid #CBD5E1', background: '#fff', color: '#475569', fontWeight: 600, fontSize: 13, cursor: 'pointer' };
